import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { fetchUserTeamStats, UserTeamStats } from '../services/stats'
import './Stats.css'

type TabType = 'totals' | 'averages'
type SortDirection = 'asc' | 'desc'
type RoundFilter = 'all' | '1' | '2' | '3' | '4' | '5' | '6'
type StatsCategorySlug =
  | 'offense'
  | 'passing'
  | 'rushing'
  | 'defense'
  | 'possession'
  | 'takeaways'
  | 'luck-score'
type StatsColumnKey =
  | 'user'
  | 'games'
  | 'record'
  | 'totalOffense'
  | 'totalPassing'
  | 'totalRushing'
  | 'totalReceiving'
  | 'passingAttempts'
  | 'passingCompletions'
  | 'passingCompletionPercentage'
  | 'passerRating'
  | 'passingTouchdowns'
  | 'passingInterceptions'
  | 'rushingAttempts'
  | 'rushingYards'
  | 'rushingYardsPerCarry'
  | 'rushingTouchdowns'
  | 'avgOffense'
  | 'avgPassing'
  | 'avgRushing'
  | 'totalDefenseAllowed'
  | 'totalPassAllowed'
  | 'totalRushAllowed'
  | 'avgDefenseAllowed'
  | 'avgPassAllowed'
  | 'avgRushAllowed'
  | 'netFumbles'
  | 'netInterceptions'
  | 'netFourthDownStops'
  | 'netTakeaways'
  | 'netTackles'
  | 'netInjuries'
  | 'netLuckScore'
  | 'totalTimeOfPossession'
  | 'opponentTimeOfPossession'
  | 'averageTimeOfPossession'
  | 'opponentAverageTimeOfPossession'

interface ColumnConfig {
  key: StatsColumnKey
  label: string
  value: (stat: UserTeamStats) => string | number
}

const MAIN_PAGE_ROW_LIMIT = 10

const STATS_CATEGORY_TITLES: Record<StatsCategorySlug, string> = {
  offense: 'Offense',
  passing: 'Passing',
  rushing: 'Rushing',
  defense: 'Defense',
  possession: 'Time of Possession',
  takeaways: 'Takeaways',
  'luck-score': "Nostra's Luck Composite Score",
}

const OFFENSE_TOTAL_COLUMNS: ColumnConfig[] = [
  { key: 'user', label: 'User', value: stat => stat.displayName.toLowerCase() },
  { key: 'games', label: 'Games', value: stat => stat.gamesPlayed },
  { key: 'totalOffense', label: 'Total Offense', value: stat => stat.totalPassingYards + stat.totalRushingYards },
  { key: 'totalPassing', label: 'Total Pass Yds', value: stat => stat.totalPassingYards },
  { key: 'totalRushing', label: 'Total Rush Yds', value: stat => stat.totalRushingYards },
]

const OFFENSE_AVERAGE_COLUMNS: ColumnConfig[] = [
  { key: 'user', label: 'User', value: stat => stat.displayName.toLowerCase() },
  { key: 'games', label: 'Games', value: stat => stat.gamesPlayed },
  { key: 'avgOffense', label: 'Avg Offense/G', value: stat => stat.passingYardsPerGame + stat.rushingYardsPerGame },
  { key: 'avgPassing', label: 'Avg Pass Yds/G', value: stat => stat.passingYardsPerGame },
  { key: 'avgRushing', label: 'Avg Rush Yds/G', value: stat => stat.rushingYardsPerGame },
]

const PASSING_COLUMNS: ColumnConfig[] = [
  { key: 'user', label: 'User', value: stat => stat.displayName.toLowerCase() },
  { key: 'games', label: 'Games', value: stat => stat.gamesPlayed },
  { key: 'passingAttempts', label: 'Attempts', value: stat => stat.passingAttempts },
  { key: 'passingCompletions', label: 'Completions', value: stat => stat.passingCompletions },
  {
    key: 'passingCompletionPercentage',
    label: 'Completion %',
    value: stat => stat.passingCompletionPercentage,
  },
  { key: 'passerRating', label: 'Passer Rating', value: stat => stat.passerRating },
  { key: 'passingTouchdowns', label: 'TD', value: stat => stat.passingTouchdowns },
  { key: 'passingInterceptions', label: 'INT', value: stat => stat.passingInterceptions },
]

const RUSHING_COLUMNS: ColumnConfig[] = [
  { key: 'user', label: 'User', value: stat => stat.displayName.toLowerCase() },
  { key: 'games', label: 'Games', value: stat => stat.gamesPlayed },
  { key: 'rushingAttempts', label: 'Attempts', value: stat => stat.rushingAttempts },
  { key: 'rushingYards', label: 'Yards', value: stat => stat.totalRushingYards },
  { key: 'rushingYardsPerCarry', label: 'YPG', value: stat => stat.rushingYardsPerCarry },
  { key: 'rushingTouchdowns', label: 'TD', value: stat => stat.rushingTouchdowns },
]

const DEFENSE_TOTAL_COLUMNS: ColumnConfig[] = [
  { key: 'user', label: 'User', value: stat => stat.displayName.toLowerCase() },
  { key: 'games', label: 'Games', value: stat => stat.gamesPlayed },
  {
    key: 'totalDefenseAllowed',
    label: 'Total Yds Allowed',
    value: stat => stat.totalPassingYardsAllowed + stat.totalRushingYardsAllowed,
  },
  { key: 'totalPassAllowed', label: 'Total Pass Yds Allowed', value: stat => stat.totalPassingYardsAllowed },
  { key: 'totalRushAllowed', label: 'Total Rush Yds Allowed', value: stat => stat.totalRushingYardsAllowed },
]

const DEFENSE_AVERAGE_COLUMNS: ColumnConfig[] = [
  { key: 'user', label: 'User', value: stat => stat.displayName.toLowerCase() },
  { key: 'games', label: 'Games', value: stat => stat.gamesPlayed },
  {
    key: 'avgDefenseAllowed',
    label: 'Avg Yds Allowed/G',
    value: stat => stat.passingYardsAllowedPerGame + stat.rushingYardsAllowedPerGame,
  },
  { key: 'avgPassAllowed', label: 'Avg Pass Yds Allowed/G', value: stat => stat.passingYardsAllowedPerGame },
  { key: 'avgRushAllowed', label: 'Avg Rush Yds Allowed/G', value: stat => stat.rushingYardsAllowedPerGame },
]

const LUCK_COLUMNS: ColumnConfig[] = [
  { key: 'user', label: 'User', value: stat => stat.displayName.toLowerCase() },
  { key: 'games', label: 'Games', value: stat => stat.gamesPlayed },
  { key: 'netFumbles', label: 'Net Fumbles', value: stat => stat.netFumbles },
  { key: 'netTackles', label: 'Net Tackles', value: stat => stat.netTackles },
  { key: 'netInjuries', label: 'Net Injuries', value: stat => stat.netInjuries },
  { key: 'netLuckScore', label: 'Net Luck Score', value: stat => stat.netLuckScore },
]

const TAKEAWAYS_COLUMNS: ColumnConfig[] = [
  { key: 'user', label: 'User', value: stat => stat.displayName.toLowerCase() },
  { key: 'games', label: 'Games', value: stat => stat.gamesPlayed },
  { key: 'netFumbles', label: 'Net Fumbles', value: stat => stat.netFumbles },
  { key: 'netInterceptions', label: 'Net INT', value: stat => stat.netInterceptions },
  { key: 'netFourthDownStops', label: 'Net 4th Stops', value: stat => stat.netFourthDownStops },
  { key: 'netTakeaways', label: 'Net Takeaways', value: stat => stat.netTakeaways },
]

const TIME_OF_POSSESSION_COLUMNS: ColumnConfig[] = [
  { key: 'user', label: 'User', value: stat => stat.displayName.toLowerCase() },
  { key: 'record', label: 'Record', value: stat => stat.winPercentage },
  { key: 'games', label: 'Games', value: stat => stat.gamesPlayed },
  { key: 'totalTimeOfPossession', label: 'Total TOP', value: stat => stat.totalTimeOfPossessionSeconds },
  { key: 'opponentTimeOfPossession', label: 'Opp Total TOP', value: stat => stat.opponentTimeOfPossessionSeconds },
  { key: 'averageTimeOfPossession', label: 'Avg TOP', value: stat => stat.averageTimeOfPossessionSeconds },
  { key: 'opponentAverageTimeOfPossession', label: 'Opp Avg TOP', value: stat => stat.opponentAverageTimeOfPossessionSeconds },
]

export default function Stats() {
  const { categorySlug } = useParams<{ categorySlug?: string }>()
  const [userStats, setUserStats] = useState<UserTeamStats[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [offenseTab, setOffenseTab] = useState<TabType>('averages')
  const [defenseTab, setDefenseTab] = useState<TabType>('averages')
  const [offenseTotalsSortColumn, setOffenseTotalsSortColumn] = useState<StatsColumnKey>('totalOffense')
  const [offenseTotalsSortDirection, setOffenseTotalsSortDirection] = useState<SortDirection>('desc')
  const [offenseAveragesSortColumn, setOffenseAveragesSortColumn] = useState<StatsColumnKey>('avgOffense')
  const [offenseAveragesSortDirection, setOffenseAveragesSortDirection] = useState<SortDirection>('desc')
  const [passingSortColumn, setPassingSortColumn] = useState<StatsColumnKey>('passerRating')
  const [passingSortDirection, setPassingSortDirection] = useState<SortDirection>('desc')
  const [rushingSortColumn, setRushingSortColumn] = useState<StatsColumnKey>('rushingYards')
  const [rushingSortDirection, setRushingSortDirection] = useState<SortDirection>('desc')
  const [defenseTotalsSortColumn, setDefenseTotalsSortColumn] = useState<StatsColumnKey>('totalDefenseAllowed')
  const [defenseTotalsSortDirection, setDefenseTotalsSortDirection] = useState<SortDirection>('asc')
  const [defenseAveragesSortColumn, setDefenseAveragesSortColumn] = useState<StatsColumnKey>('avgDefenseAllowed')
  const [defenseAveragesSortDirection, setDefenseAveragesSortDirection] = useState<SortDirection>('asc')
  const [luckSortColumn, setLuckSortColumn] = useState<StatsColumnKey>('netLuckScore')
  const [luckSortDirection, setLuckSortDirection] = useState<SortDirection>('desc')
  const [takeawaysSortColumn, setTakeawaysSortColumn] = useState<StatsColumnKey>('netTakeaways')
  const [takeawaysSortDirection, setTakeawaysSortDirection] = useState<SortDirection>('desc')
  const [possessionSortColumn, setPossessionSortColumn] = useState<StatsColumnKey>('averageTimeOfPossession')
  const [possessionSortDirection, setPossessionSortDirection] = useState<SortDirection>('desc')
  const [roundFilter, setRoundFilter] = useState<RoundFilter>('all')
  const selectedCategory = getStatsCategorySlug(categorySlug)
  const isCategoryPage = Boolean(categorySlug)
  const rowLimit = isCategoryPage ? undefined : MAIN_PAGE_ROW_LIMIT
  const tableStats =
    isCategoryPage && roundFilter !== 'all'
      ? userStats.filter(stat => getDraftRound(stat.groupPlaySlot) === roundFilter)
      : userStats

  useEffect(() => {
    let isMounted = true

    async function loadStats() {
      try {
        const data = await fetchUserTeamStats()
        if (isMounted) {
          setUserStats(data)
          setError(null)
        }
      } catch (err: any) {
        console.error('Failed to load user stats', err)
        if (isMounted) {
          setError(err?.response?.data?.error ?? 'Failed to load stats')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadStats()

    return () => {
      isMounted = false
    }
  }, [])

  const renderSortIcon = (isActive: boolean, direction: SortDirection) => {
    if (!isActive) {
      return <span className="sort-icon sort-icon-inactive">↕</span>
    }

    return (
      <span
        className="sort-icon"
        aria-label={`Sorted ${direction === 'asc' ? 'ascending' : 'descending'}`}
      >
        {direction === 'asc' ? '↑' : '↓'}
      </span>
    )
  }

  const sortStats = (
    stats: UserTeamStats[],
    columns: ColumnConfig[],
    sortColumn: StatsColumnKey,
    sortDirection: SortDirection
  ) => {
    const activeColumn = columns.find(column => column.key === sortColumn) ?? columns[0]
    if (!activeColumn) {
      return stats
    }

    return [...stats].sort((a, b) => {
      const aValue = activeColumn.value(a)
      const bValue = activeColumn.value(b)

      let comparison = 0
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue)
      } else {
        comparison = Number(aValue) - Number(bValue)
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })
  }

  const renderUserCell = (stat: UserTeamStats) => (
    <div className="user-cell">
      {stat.discordAvatar && stat.discordId ? (
        <img
          src={`https://cdn.discordapp.com/avatars/${stat.discordId}/${stat.discordAvatar}.png?size=64`}
          alt={stat.displayName}
          className="user-avatar"
          loading="lazy"
        />
      ) : (
        <div className="user-avatar-placeholder">
          {stat.displayName.charAt(0).toUpperCase()}
        </div>
      )}
      <div className="user-text">
        <span className="user-display">{stat.displayName}</span>
        {stat.displayName !== stat.username && (
          <span className="user-username">@{stat.username}</span>
        )}
      </div>
    </div>
  )

  const renderCell = (stat: UserTeamStats, columnKey: StatsColumnKey) => {
    switch (columnKey) {
      case 'user':
        return renderUserCell(stat)
      case 'games':
        return stat.gamesPlayed
      case 'record':
        return `${stat.wins}-${stat.losses}${stat.ties > 0 ? `-${stat.ties}` : ''}`
      case 'totalOffense':
        return formatYards(stat.totalPassingYards + stat.totalRushingYards)
      case 'totalPassing':
        return formatYards(stat.totalPassingYards)
      case 'totalRushing':
        return formatYards(stat.totalRushingYards)
      case 'totalReceiving':
        return formatYards(stat.totalReceivingYards)
      case 'passingAttempts':
        return stat.passingAttempts
      case 'passingCompletions':
        return stat.passingCompletions
      case 'passingCompletionPercentage':
        return formatPercentage(stat.passingCompletionPercentage)
      case 'passerRating':
        return formatAverage(stat.passerRating)
      case 'passingTouchdowns':
        return stat.passingTouchdowns
      case 'passingInterceptions':
        return stat.passingInterceptions
      case 'rushingAttempts':
        return stat.rushingAttempts
      case 'rushingYards':
        return formatYards(stat.totalRushingYards)
      case 'rushingYardsPerCarry':
        return formatAverage(stat.rushingYardsPerCarry)
      case 'rushingTouchdowns':
        return stat.rushingTouchdowns
      case 'avgOffense':
        return formatAverage(stat.passingYardsPerGame + stat.rushingYardsPerGame)
      case 'avgPassing':
        return formatAverage(stat.passingYardsPerGame)
      case 'avgRushing':
        return formatAverage(stat.rushingYardsPerGame)
      case 'totalDefenseAllowed':
        return formatYards(stat.totalPassingYardsAllowed + stat.totalRushingYardsAllowed)
      case 'totalPassAllowed':
        return formatYards(stat.totalPassingYardsAllowed)
      case 'totalRushAllowed':
        return formatYards(stat.totalRushingYardsAllowed)
      case 'avgDefenseAllowed':
        return formatAverage(stat.passingYardsAllowedPerGame + stat.rushingYardsAllowedPerGame)
      case 'avgPassAllowed':
        return formatAverage(stat.passingYardsAllowedPerGame)
      case 'avgRushAllowed':
        return formatAverage(stat.rushingYardsAllowedPerGame)
      case 'netFumbles':
        return formatSignedNumber(stat.netFumbles)
      case 'netInterceptions':
        return formatSignedNumber(stat.netInterceptions)
      case 'netFourthDownStops':
        return formatSignedNumber(stat.netFourthDownStops)
      case 'netTakeaways':
        return formatSignedNumber(stat.netTakeaways)
      case 'netTackles':
        return formatSignedNumber(stat.netTackles)
      case 'netInjuries':
        return formatSignedNumber(stat.netInjuries)
      case 'netLuckScore':
        return formatSignedNumber(stat.netLuckScore)
      case 'totalTimeOfPossession':
        return formatTimeOfPossession(stat.totalTimeOfPossessionSeconds)
      case 'opponentTimeOfPossession':
        return formatTimeOfPossession(stat.opponentTimeOfPossessionSeconds)
      case 'averageTimeOfPossession':
        return formatTimeOfPossession(stat.averageTimeOfPossessionSeconds)
      case 'opponentAverageTimeOfPossession':
        return formatTimeOfPossession(stat.opponentAverageTimeOfPossessionSeconds)
    }
  }

  const renderTable = (
    columns: ColumnConfig[],
    sortColumn: StatsColumnKey,
    sortDirection: SortDirection,
    onSort: (column: StatsColumnKey) => void,
    maxRows?: number
  ) => {
    const sortedStats = sortStats(tableStats, columns, sortColumn, sortDirection)
    const visibleStats = typeof maxRows === 'number'
      ? sortedStats.slice(0, maxRows)
      : sortedStats

    return (
      <div className="table-wrapper">
        <table className="stats-table">
          <thead>
            <tr>
              {columns.map(column => (
                <th
                  key={column.key}
                  className="sortable"
                  onClick={() => onSort(column.key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onSort(column.key)
                    }
                  }}
                >
                  {column.label}
                  {renderSortIcon(sortColumn === column.key, sortDirection)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleStats.length > 0 ? (
              visibleStats.map(stat => (
                <tr key={stat.userId}>
                  {columns.map(column => (
                    <td key={`${stat.userId}-${column.key}`}>{renderCell(stat, column.key)}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="stats-empty-cell">
                  No players match this round filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )
  }

  const renderSeeAllLink = (category: StatsCategorySlug) => {
    if (isCategoryPage || userStats.length <= MAIN_PAGE_ROW_LIMIT) {
      return null
    }

    return (
      <Link to={`/stats/${category}`} className="stats-see-all-link">
        See all
      </Link>
    )
  }

  const renderSectionHeader = (
    title: string,
    subtitle: string,
    category: StatsCategorySlug,
    titleContent?: ReactNode
  ) => (
    <header className="stats-section-header">
      <div>
        {titleContent ?? <h2>{title}</h2>}
        <p className="section-subtitle">{subtitle}</p>
      </div>
      {renderSeeAllLink(category)}
    </header>
  )

  const renderRoundFilter = () => {
    if (!isCategoryPage || !selectedCategory) {
      return null
    }

    return (
      <div className="stats-round-filter">
        <label htmlFor="stats-round-filter">Draft Round</label>
        <select
          id="stats-round-filter"
          value={roundFilter}
          onChange={event => setRoundFilter(event.target.value as RoundFilter)}
        >
          <option value="all">All rounds</option>
          <option value="1">Round 1</option>
          <option value="2">Round 2</option>
          <option value="3">Round 3</option>
          <option value="4">Round 4</option>
          <option value="5">Round 5</option>
          <option value="6">Round 6</option>
        </select>
      </div>
    )
  }

  const toggleSort = (
    column: StatsColumnKey,
    currentColumn: StatsColumnKey,
    currentDirection: SortDirection,
    setColumn: (value: StatsColumnKey) => void,
    setDirection: (value: SortDirection) => void
  ) => {
    if (currentColumn === column) {
      setDirection(currentDirection === 'asc' ? 'desc' : 'asc')
      return
    }

    setColumn(column)
    setDirection('desc')
  }

  const renderSectionBody = (
    tab: TabType,
    setTab: (value: TabType) => void,
    totalColumns: ColumnConfig[],
    totalSortColumn: StatsColumnKey,
    totalSortDirection: SortDirection,
    setTotalSortColumn: (value: StatsColumnKey) => void,
    setTotalSortDirection: (value: SortDirection) => void,
    averageColumns: ColumnConfig[],
    averageSortColumn: StatsColumnKey,
    averageSortDirection: SortDirection,
    setAverageSortColumn: (value: StatsColumnKey) => void,
    setAverageSortDirection: (value: SortDirection) => void,
    maxRows?: number
  ) => (
    <>
      <div className="tabs">
        <button
          className={`tab ${tab === 'averages' ? 'active' : ''}`}
          onClick={() => setTab('averages')}
          type="button"
        >
          Average Per Game
        </button>
        <button
          className={`tab ${tab === 'totals' ? 'active' : ''}`}
          onClick={() => setTab('totals')}
          type="button"
        >
          Total / Cumulative
        </button>
      </div>
      <div className="tab-content">
        {tab === 'averages' &&
          renderTable(
            averageColumns,
            averageSortColumn,
            averageSortDirection,
            column =>
              toggleSort(
                column,
                averageSortColumn,
                averageSortDirection,
                setAverageSortColumn,
                setAverageSortDirection
              ),
            maxRows
          )}
        {tab === 'totals' &&
          renderTable(
            totalColumns,
            totalSortColumn,
            totalSortDirection,
            column =>
              toggleSort(
                column,
                totalSortColumn,
                totalSortDirection,
                setTotalSortColumn,
                setTotalSortDirection
              ),
            maxRows
          )}
      </div>
    </>
  )

  const renderOffenseSection = (maxRows?: number) => (
    <section id="stats-offense" className="section stats-anchor-section">
      {renderSectionHeader(
        'Offense',
        'Total production and per-game offensive output for each coach.',
        'offense'
      )}
      {renderSectionBody(
        offenseTab,
        setOffenseTab,
        OFFENSE_TOTAL_COLUMNS,
        offenseTotalsSortColumn,
        offenseTotalsSortDirection,
        setOffenseTotalsSortColumn,
        setOffenseTotalsSortDirection,
        OFFENSE_AVERAGE_COLUMNS,
        offenseAveragesSortColumn,
        offenseAveragesSortDirection,
        setOffenseAveragesSortColumn,
        setOffenseAveragesSortDirection,
        maxRows
      )}
    </section>
  )

  const renderPassingSection = (maxRows?: number) => (
    <section id="stats-passing" className="section stats-anchor-section">
      {renderSectionHeader(
        'Passing',
        'Attempts, completions, completion rate, touchdowns, and interceptions for each coach.',
        'passing'
      )}
      {renderTable(
        PASSING_COLUMNS,
        passingSortColumn,
        passingSortDirection,
        column =>
          toggleSort(
            column,
            passingSortColumn,
            passingSortDirection,
            setPassingSortColumn,
            setPassingSortDirection
          ),
        maxRows
      )}
    </section>
  )

  const renderRushingSection = (maxRows?: number) => (
    <section id="stats-rushing" className="section stats-anchor-section">
      {renderSectionHeader(
        'Rushing',
        'Attempts, yards, yards per carry, and touchdowns for each coach.',
        'rushing'
      )}
      {renderTable(
        RUSHING_COLUMNS,
        rushingSortColumn,
        rushingSortDirection,
        column =>
          toggleSort(
            column,
            rushingSortColumn,
            rushingSortDirection,
            setRushingSortColumn,
            setRushingSortDirection
          ),
        maxRows
      )}
    </section>
  )

  const renderDefenseSection = (maxRows?: number) => (
    <section id="stats-defense" className="section stats-anchor-section">
      {renderSectionHeader(
        'Defense',
        'Passing and rushing yards allowed totals plus per-game defensive averages.',
        'defense'
      )}
      {renderSectionBody(
        defenseTab,
        setDefenseTab,
        DEFENSE_TOTAL_COLUMNS,
        defenseTotalsSortColumn,
        defenseTotalsSortDirection,
        setDefenseTotalsSortColumn,
        setDefenseTotalsSortDirection,
        DEFENSE_AVERAGE_COLUMNS,
        defenseAveragesSortColumn,
        defenseAveragesSortDirection,
        setDefenseAveragesSortColumn,
        setDefenseAveragesSortDirection,
        maxRows
      )}
    </section>
  )

  const renderPossessionSection = (maxRows?: number) => (
    <section id="stats-possession" className="section stats-anchor-section">
      {renderSectionHeader(
        'Time of Possession',
        'Record, total possession, opponent possession, and per-game possession averages for each coach.',
        'possession'
      )}
      {renderTable(
        TIME_OF_POSSESSION_COLUMNS,
        possessionSortColumn,
        possessionSortDirection,
        column =>
          toggleSort(
            column,
            possessionSortColumn,
            possessionSortDirection,
            setPossessionSortColumn,
            setPossessionSortDirection
          ),
        maxRows
      )}
    </section>
  )

  const renderTakeawaysSection = (maxRows?: number) => (
    <section id="stats-takeaways" className="section stats-anchor-section">
      {renderSectionHeader(
        'Takeaways (inc. 4th down stops)',
        'Net fumbles recovered, interceptions made, and 4th down stops for each coach.',
        'takeaways'
      )}
      {renderTable(
        TAKEAWAYS_COLUMNS,
        takeawaysSortColumn,
        takeawaysSortDirection,
        column =>
          toggleSort(
            column,
            takeawaysSortColumn,
            takeawaysSortDirection,
            setTakeawaysSortColumn,
            setTakeawaysSortDirection
          ),
        maxRows
      )}
    </section>
  )

  const renderLuckSection = (maxRows?: number) => (
    <section id="stats-luck-score" className="section stats-anchor-section">
      {renderSectionHeader(
        "Nostra's Luck Composite Score",
        'Net fumbles are worth +/-100 each, net injuries are worth +/-50 each in Net Luck Score, while net positive tackles subtract 2 points each and net negative tackles add 2 points each.',
        'luck-score',
        <h2 className="luck-score-title">
          <img src="/horseshoe.png" alt="" className="luck-score-icon" />
          <span>Nostra&apos;s Luck Composite Score</span>
        </h2>
      )}
      {renderTable(
        LUCK_COLUMNS,
        luckSortColumn,
        luckSortDirection,
        column =>
          toggleSort(
            column,
            luckSortColumn,
            luckSortDirection,
            setLuckSortColumn,
            setLuckSortDirection
          ),
        maxRows
      )}
    </section>
  )

  const renderCategorySection = (category: StatsCategorySlug, maxRows?: number) => {
    switch (category) {
      case 'offense':
        return renderOffenseSection(maxRows)
      case 'passing':
        return renderPassingSection(maxRows)
      case 'rushing':
        return renderRushingSection(maxRows)
      case 'defense':
        return renderDefenseSection(maxRows)
      case 'possession':
        return renderPossessionSection(maxRows)
      case 'takeaways':
        return renderTakeawaysSection(maxRows)
      case 'luck-score':
        return renderLuckSection(maxRows)
    }
  }

  const renderPageBody = () => {
    if (loading) {
      return <p className="info-message">Loading stats...</p>
    }

    if (error) {
      return <div className="message message-error">{error}</div>
    }

    if (userStats.length === 0) {
      return <p className="info-message">No stats available yet.</p>
    }

    if (isCategoryPage && !selectedCategory) {
      return <div className="message message-error">Unknown stats category.</div>
    }

    if (selectedCategory) {
      return (
        <div className="stats-sections">
          <Link to="/stats" className="stats-back-link">Back to Stats</Link>
          {renderRoundFilter()}
          {renderCategorySection(selectedCategory)}
        </div>
      )
    }

    return (
      <div className="stats-sections">
        <nav className="stats-section-nav" aria-label="Stats sections">
          <a href="#stats-offense" className="btn btn-secondary">Offense</a>
          <a href="#stats-passing" className="btn btn-secondary">Passing</a>
          <a href="#stats-rushing" className="btn btn-secondary">Rushing</a>
          <a href="#stats-defense" className="btn btn-secondary">Defense</a>
          <a href="#stats-possession" className="btn btn-secondary">TOP</a>
          <a href="#stats-takeaways" className="btn btn-secondary">Takeaways</a>
          <a href="#stats-luck-score" className="btn btn-secondary">Luck Score</a>
        </nav>
        {renderOffenseSection(rowLimit)}
        {renderPassingSection(rowLimit)}
        {renderRushingSection(rowLimit)}
        {renderDefenseSection(rowLimit)}
        {renderPossessionSection(rowLimit)}
        {renderTakeawaysSection(rowLimit)}
        {renderLuckSection(rowLimit)}
      </div>
    )
  }

  return (
    <div className="stats-page">
      <h1>{selectedCategory ? `${STATS_CATEGORY_TITLES[selectedCategory]} Stats` : 'Stats'}</h1>
      {renderPageBody()}
    </div>
  )
}

function formatYards(value: number) {
  return Math.round(value).toLocaleString()
}

function getStatsCategorySlug(value: string | undefined): StatsCategorySlug | null {
  if (
    value === 'offense' ||
    value === 'passing' ||
    value === 'rushing' ||
    value === 'defense' ||
    value === 'possession' ||
    value === 'takeaways' ||
    value === 'luck-score'
  ) {
    return value
  }

  return null
}

function getDraftRound(groupPlaySlot: string | null | undefined): RoundFilter | null {
  const round = groupPlaySlot?.split('.')[0]

  if (
    round === '1' ||
    round === '2' ||
    round === '3' ||
    round === '4' ||
    round === '5' ||
    round === '6'
  ) {
    return round
  }

  return null
}

function formatAverage(value: number) {
  return value.toFixed(1)
}

function formatPercentage(value: number) {
  return `${(value * 100).toFixed(1)}%`
}

function formatTimeOfPossession(value: number) {
  const totalSeconds = Math.max(0, Math.round(value))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

function formatSignedNumber(value: number) {
  return `${value > 0 ? '+' : ''}${value.toLocaleString()}`
}

